import React from "react";

import { Ban, PackageMinus, SlidersHorizontal } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Label } from "~/components/ui/label";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { cn } from "~/lib/utils";

export type CreditNoteType = "full" | "partial" | "administrative";

interface CreditNoteTypeSelectorProps {
  selectedType: CreditNoteType;
  onTypeChange: (type: CreditNoteType) => void;
  disabled?: boolean;
}

const CREDIT_NOTE_TYPES: {
  value: CreditNoteType;
  icon: React.ElementType;
  title: string;
  description: string;
  note: string;
}[] = [
  {
    value: "full",
    icon: Ban,
    title: "Anulación Completa",
    description:
      "Anula completamente la factura. Genera una nota de crédito por el monto total del documento.",
    note: "Marca la factura como completamente anulada.",
  },
  {
    value: "partial",
    icon: PackageMinus,
    title: "Nota de Crédito Parcial",
    description:
      "Genera una nota de crédito por cantidades específicas de productos. La factura original permanece activa con el saldo reducido.",
    note: "La factura original permanece válida.",
  },
  {
    value: "administrative",
    icon: SlidersHorizontal,
    title: "Nota de Crédito Administrativa",
    description:
      "Genera una nota de crédito por un monto fijo para ajustes administrativos, descuentos especiales o correcciones.",
    note: "Para ajustes y correcciones administrativas.",
  },
];

export const CreditNoteTypeSelector: React.FC<CreditNoteTypeSelectorProps> = ({
  selectedType,
  onTypeChange,
  disabled = false,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Tipo de Nota de Crédito</CardTitle>
        <CardDescription>
          Selecciona el tipo de nota de crédito que deseas generar
        </CardDescription>
      </CardHeader>
      <CardContent>
        <RadioGroup
          value={selectedType}
          onValueChange={(value) => onTypeChange(value as CreditNoteType)}
          disabled={disabled}
          className="space-y-3"
        >
          {CREDIT_NOTE_TYPES.map(
            ({ value, icon: Icon, title, description, note }) => {
              const isSelected = selectedType === value;
              return (
                <Label
                  key={value}
                  htmlFor={value}
                  className={cn(
                    "flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors",
                    isSelected
                      ? "border-primary bg-muted/50"
                      : "hover:bg-muted/40",
                    disabled && "cursor-not-allowed opacity-60"
                  )}
                >
                  <RadioGroupItem value={value} id={value} className="mt-1" />
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="text-base font-medium text-foreground">
                        {title}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {description}
                    </p>
                    <p className="text-xs text-muted-foreground">{note}</p>
                  </div>
                </Label>
              );
            }
          )}
        </RadioGroup>
      </CardContent>
    </Card>
  );
};
